import {
  SIGNATURE_BLOCK_DESCRIPTIONS,
  SIGNATURE_BLOCK_LABELS,
  type PrintSignatures,
  type SignatureBlock,
  type SignatureBlockKey,
  signatureBlockKeySchema,
} from '#shared/types';
import { Checkbox } from '#client/components/ui/Checkbox';
import { Select } from '#client/components/ui/Select';
import { TextField } from '#client/components/ui/TextField';
import { NumberField } from '../controls/NumberField';
import { ALIGNMENT_OPTIONS } from './alignment-options';

interface SignaturesSectionProps {
  signatures: PrintSignatures;
  onChange: (patch: Partial<PrintSignatures>) => void;
}

/**
 * The signatories at the foot of every page.
 *
 * Who created the report (entered the results), who approved it, and the
 * reporting doctor — read from the reports, with each person's own signature
 * scan from their staff or consultant record. They print whether or not the
 * footer does.
 *
 * Every block is offered whether or not the lab currently records that person,
 * because `show_when_empty` decides whether an unrecorded one prints. A lab that
 * has not started naming a reporting doctor sees the block configured here and
 * nothing on paper, which is the honest way round: the setting is discoverable
 * and the sheet does not gain an empty box.
 */
export function SignaturesSection({ signatures, onChange }: SignaturesSectionProps) {
  const byKey = new Map(signatures.blocks.map((block) => [block.key, block]));

  function replace(key: SignatureBlockKey, changes: Partial<SignatureBlock>) {
    const existing = byKey.get(key);
    if (!existing) return;

    onChange({
      blocks: signatures.blocks.map((block) =>
        block.key === key ? { ...block, ...changes } : block,
      ),
    });
  }

  return (
    <>
      <Checkbox
        label="Print signatories at the foot of every page"
        checked={signatures.enabled}
        onChange={(event) => onChange({ enabled: event.target.checked })}
      />

      {signatures.enabled && (
        <>
          <SharedFields signatures={signatures} onChange={onChange} />

          {signatureBlockKeySchema.options.map((key: SignatureBlockKey) => {
            const block = byKey.get(key);
            if (!block) return null;
            return (
              <BlockFields key={key} block={block} onChange={(changes) => replace(key, changes)} />
            );
          })}
        </>
      )}
    </>
  );
}

/** What applies to every block: when they print, the rule, and the sizes. */
function SharedFields({ signatures, onChange }: SignaturesSectionProps) {
  return (
    <>
      <Checkbox
        label="Print a block even when nobody is recorded"
        checked={signatures.show_when_empty}
        onChange={(event) => onChange({ show_when_empty: event.target.checked })}
      />
      <p className="c-field__help">
        Off, a block appears only once a report on the sheet has that person recorded. On, every
        enabled block prints, with a line to sign by hand. Tests signed by different people list
        every name and print no scan.
      </p>

      <Checkbox
        label="Draw a line under blocks with no signature image"
        checked={signatures.rule}
        onChange={(event) => onChange({ rule: event.target.checked })}
      />

      <div className="c-form-grid">
        <NumberField
          label="Signature width"
          unit="mm"
          value={signatures.image_width}
          onChange={(image_width) => onChange({ image_width })}
          min={0}
          max={120}
          step={1}
        />
        <NumberField
          label="Signature height"
          unit="mm"
          value={signatures.image_height}
          onChange={(image_height) => onChange({ image_height })}
          min={0}
          max={40}
          step={1}
          helpText="A scan is fitted inside this box, never stretched to fill it"
        />
        <NumberField
          label="Text size"
          unit="pt"
          value={signatures.size}
          onChange={(size) => onChange({ size })}
          min={4}
          max={20}
          step={0.5}
        />
      </div>
    </>
  );
}

/** One signatory's block: whether it prints, and what it shows. */
function BlockFields({
  block,
  onChange,
}: {
  block: SignatureBlock;
  onChange: (changes: Partial<SignatureBlock>) => void;
}) {
  return (
    <div className="c-field-rows">
      <Checkbox
        label={SIGNATURE_BLOCK_LABELS[block.key]}
        checked={block.enabled}
        onChange={(event) => onChange({ enabled: event.target.checked })}
      />
      <p className="c-field__help">{SIGNATURE_BLOCK_DESCRIPTIONS[block.key]}</p>

      {block.enabled && (
        <div className="c-form-grid">
          <TextField
            label="Caption"
            value={block.label}
            onChange={(event) => onChange({ label: event.target.value })}
            helpText="Printed above the name. Blank hides it."
          />
          <Select
            label="Align"
            options={ALIGNMENT_OPTIONS}
            value={block.align}
            onChange={(choice) => onChange({ align: choice })}
          />
          <Checkbox
            label="Show the signature"
            checked={block.show_image}
            onChange={(event) => onChange({ show_image: event.target.checked })}
          />
          <Checkbox
            label="Show qualifications"
            checked={block.show_qualifications}
            onChange={(event) => onChange({ show_qualifications: event.target.checked })}
          />
          {block.key === 'consultant' && (
            <Checkbox
              label="Show the registration number"
              checked={block.show_registration}
              onChange={(event) => onChange({ show_registration: event.target.checked })}
            />
          )}
          {block.key === 'verified_by' && (
            <Checkbox
              label="Show the completion time"
              checked={block.show_timestamp}
              onChange={(event) => onChange({ show_timestamp: event.target.checked })}
            />
          )}
        </div>
      )}
    </div>
  );
}
